import React, { useState, useEffect, useRef } from "react";
import classes from "./UploadExcelModal3.module.css";
import { X, Download, Upload, CheckCircle } from "lucide-react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import api from "../../Components/API/Api"; // Adjust path if necessary

const UploadExcelModal3 = ({ isOpen, onClose, companyId, onUploadSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState(null);
  const [transactionDate, setTransactionDate] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFile(null);
      setTransactionDate("");
      setBankAccount("");
      fetchBankAccounts();
    }
  }, [isOpen]);

  // Fetch bank accounts for the dropdown
  const fetchBankAccounts = async () => {
    if (!companyId) return;
    setIsLoadingDropdowns(true);
    try {
      const res = await api.get(`/v1/bank-accounts?company_id=${companyId}`);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setBankAccounts(data);
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      setBankAccounts([]);
    } finally {
      setIsLoadingDropdowns(false);
    }
  };

  // Download Template - Exact API provided
  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get('v1/saving_withdrawal_template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'saving_withdrawal_template.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setCurrentStep(2);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: error.response?.data?.message || 'Could not download the template.'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // File handlers
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  // Submit to API - Exact API & params provided
  const handleSubmit = async () => {
    if (!file) {
      Swal.fire({ icon: 'error', title: 'Missing File', text: 'Please upload the completed Excel file.' });
      return;
    }
    if (!transactionDate) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Transaction Date.' });
      return;
    }
    if (!bankAccount) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Bank Account.' });
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('company_id', companyId);
      formData.append('transaction_date', transactionDate);
      formData.append('bank', bankAccount);
      formData.append('file', file);

      const res = await api.post('/customer/upload-savings-withdrawal', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Upload Successful',
          text: res.data?.message || 'Savings withdrawals uploaded successfully.'
        });
        if (onUploadSuccess) onUploadSuccess();
        onClose();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: error.response?.data?.message || 'Failed to upload the file.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div className={classes.customModal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.modalContent}>
          <div className={classes.modalHeader}>
            <div>
              <h5 className={classes.modalTitle}>Upload Savings Withdrawals</h5>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>
                Two steps to upload bulk savings withdrawals
              </p>
            </div>
            <button type="button" className={classes.modalCloseBtn} onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className={classes.modalBody} style={{ paddingTop: '10px' }}>
            {/* --- STEP 1 --- */}
            {currentStep === 1 && (
              <>
                <div className={classes.dashedBox}>
                  <div className={classes.iconGreenBox}><Download size={24} /></div>
                  <h4 style={{ margin: '10px 0 5px', fontSize: '14px', fontWeight: 600 }}>saving_withdrawal_template.xlsx</h4>
                  <p style={{ fontSize: '13px', color: '#4b5563', maxWidth: '400px', margin: '10px auto 0', lineHeight: 1.5 }}>
                    Download our ready-made template, fill in your data offline at your own pace, then re-upload.
                  </p>
                </div>
                <div className={classes.stepsList}>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Click Download Template</div>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Open the file and fill in members' withdrawal details</div>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Click Upload document and upload the completed file</div>
                </div>
                <div className={classes.modalFooter} style={{ borderTop: '1px solid #e5e7eb', marginTop: '20px', paddingTop: '16px' }}>
                  <div className={classes.modalBtnRow} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <button type="button" className={classes.btnUploadOutline} onClick={() => setCurrentStep(2)}>
                      <Upload size={18} style={{ marginRight: '6px' }} /> Upload Document
                    </button>
                    <button type="button" className={classes.btnUploadSolid} onClick={handleDownloadTemplate} disabled={isDownloading}>
                      {isDownloading ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                           <Spinner animation="border" size="sm" /> Downloading...
                        </span>
                      ) : (
                        <><Download size={18} style={{ marginRight: '6px' }} /> Download Template</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* --- STEP 2 --- */}
            {currentStep === 2 && (
              <>
                <div className={classes.successHeader}>
                  <CheckCircle size={22} className={classes.greenIcon} fill="#16A34A" color="white" />
                  <span>Template downloaded - now upload your completed file below</span>
                </div>
                <div className={classes.uploadDashedBox} onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={handleDragOver}>
                  <div className={classes.uploadIconBox}><Upload size={24} /></div>
                  <h4 style={{ margin: '0', fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>Click to upload your completed file</h4>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>or drag and drop .xlsx, .xls accepted</p>
                  <input type="file" accept=".xlsx,.xls" hidden ref={fileInputRef} onChange={handleFileSelect} />
                  {file && <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 500, color: '#16A34A' }}>✓ {file.name}</div>}
                </div>

                {/* === FORM FIELDS === */}
                <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div className={classes.modalRow3}>
                    <div className={classes.modalCol}>
                      <label>Transaction Date</label>
                      <input type="date" className={classes.modalInput} value={transactionDate} onChange={(e) => setTransactionDate(e.target.value)} />
                    </div>
                    <div className={classes.modalCol}>
                      <label>Bank Account</label>
                      <select className={classes.modalInput} value={bankAccount} onChange={(e) => setBankAccount(e.target.value)}>
                        <option value="">Select Bank Account</option>
                        {isLoadingDropdowns ? (
                          <option>Loading...</option>
                        ) : bankAccounts.map(b => (
                          <option key={b.id} value={b.id}>{b.gl_name || b.name || b.account_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className={classes.modalFooter} style={{ borderTop: '1px solid #e5e7eb', marginTop: '20px', paddingTop: '16px' }}>
                  <div className={classes.modalBtnRow} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <button type="button" className={classes.btnUploadOutline} onClick={onClose}>Cancel</button>
                    <button type="button" className={classes.btnUploadSolid} onClick={handleSubmit} disabled={isSaving}>
                      {isSaving ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><Spinner animation="border" size="sm" /> Saving...</span>
                      ) : ( "Save" )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadExcelModal3;